import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { DISCOVERY_SECTIONS as AUDIT_SECTIONS, TOTAL_QUESTIONS, getVisibleQuestions } from "../data/questions";
import { INDUSTRIES } from "../data/industries";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, ArrowRight, Loader2, Save, Zap, X, GitBranch, CheckCircle, Target, TrendingUp, Settings, Rocket } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { trackAuditStarted } from "../lib/analytics";
import { usePageSEO } from "../lib/seo";

export default function AuditWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: "Growth Blueprint", noIndex: true });
  const [auditId, setAuditId] = useState(null);
  const [step, setStep] = useState("intro");
  const [companyInfo, setCompanyInfo] = useState({ company_name: "", industry: "", sub_industry: "", company_size: "", annual_revenue: "", website: "", business_description: "" });
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);

  useEffect(() => {
    const loadOrCreate = async () => {
      try {
        // Check for admin override audit_id in URL params
        const params = new URLSearchParams(window.location.search);
        const paramAuditId = params.get("audit_id");

        if (paramAuditId && user?.role === "admin") {
          // Admin mode: load specific audit
          setIsAdminMode(true);
          setAuditId(paramAuditId);
          try {
            const { data: audit } = await api.get(`/audit/admin/${paramAuditId}`);
            if (audit.company_info) setCompanyInfo(audit.company_info);
            if (audit.answers) setAnswers(audit.answers);
            if (audit.current_section && audit.current_section !== "company_info") setStep(audit.current_section);
            else setStep("company_info");
          } catch (e) {
            console.error("Admin audit load failed:", e);
          }
        } else {
          // Normal flow: find user's in-progress audit
          const { data: audits } = await api.get("/audit/user/list");
          const inProgress = audits.find((a) => a.status === "in_progress");
          if (inProgress) {
            setAuditId(inProgress.audit_id);
            const { data: audit } = await api.get(`/audit/${inProgress.audit_id}`);
            if (audit.company_info) setCompanyInfo(audit.company_info);
            if (audit.answers) setAnswers(audit.answers);
            if (audit.current_section && audit.current_section !== "company_info") setStep(audit.current_section);
            else setStep("company_info");
          }
        }
      } catch (err) {
        console.error("Error loading audit:", err);
      } finally {
        setLoading(false);
      }
    };
    loadOrCreate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sectionIndex = AUDIT_SECTIONS.findIndex((s) => s.id === step);
  const currentSection = AUDIT_SECTIONS[sectionIndex] || null;
  const totalSections = AUDIT_SECTIONS.length;

  // Get visible questions for the current section (conditional branching)
  const visibleQuestions = useMemo(() => {
    if (!currentSection) return [];
    return getVisibleQuestions(currentSection, answers[step] || {});
  }, [currentSection, answers, step]);

  // Calculate progress across all sections
  const progress = useMemo(() => {
    if (step === "intro" || step === "company_info") return 0;
    let totalVisible = 0;
    let totalAnswered = 0;
    for (const section of AUDIT_SECTIONS) {
      const sectionAnswers = answers[section.id] || {};
      const visible = getVisibleQuestions(section, sectionAnswers);
      totalVisible += visible.length;
      totalAnswered += visible.filter(q => sectionAnswers[q.id] !== undefined && sectionAnswers[q.id] !== "").length;
    }
    return totalVisible > 0 ? Math.round((totalAnswered / totalVisible) * 100) : 0;
  }, [answers, step]);

  const startAudit = async () => {
    try {
      if (isAdminMode && auditId) {
        // Admin mode: update company info on existing audit
        await api.post(`/audit/admin/edit/${auditId}`, { company_info: companyInfo });
        setStep(AUDIT_SECTIONS[0].id);
      } else {
        const { data } = await api.post("/audit/start", companyInfo);
        setAuditId(data.audit_id);
        setStep(AUDIT_SECTIONS[0].id);
        trackAuditStarted(companyInfo.company_name, companyInfo.industry);
      }
    } catch (err) {
      console.error("Error starting audit:", err);
    }
  };

  const saveSection = useCallback(async (sectionId, sectionAnswers) => {
    if (!auditId) return;
    setSaving(true);
    try {
      if (isAdminMode) {
        await api.post("/audit/admin/save", { audit_id: auditId, section: sectionId, answers: sectionAnswers });
      } else {
        await api.post("/audit/save", { audit_id: auditId, section: sectionId, answers: sectionAnswers });
      }
    } catch (err) {
      console.error("Error saving:", err);
    } finally {
      setSaving(false);
    }
  }, [auditId, isAdminMode]);

  const handleAnswer = (questionId, value) => {
    const sectionId = step;
    const newSectionAnswers = { ...answers[sectionId], [questionId]: value };
    const newAnswers = { ...answers, [sectionId]: newSectionAnswers };
    setAnswers(newAnswers);
    saveSection(sectionId, newSectionAnswers);
  };

  const handleNext = () => {
    if (!currentSection || visibleQuestions.length === 0) return;
    if (currentQuestion < visibleQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      if (sectionIndex < totalSections - 1) {
        setStep(AUDIT_SECTIONS[sectionIndex + 1].id);
        setCurrentQuestion(0);
      } else {
        handleComplete();
      }
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (sectionIndex > 0) {
      const prevSection = AUDIT_SECTIONS[sectionIndex - 1];
      const prevVisible = getVisibleQuestions(prevSection, answers[prevSection.id] || {});
      setStep(prevSection.id);
      setCurrentQuestion(Math.max(0, prevVisible.length - 1));
    } else {
      setStep("company_info");
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await api.post("/audit/complete", { audit_id: auditId });
      navigate(`/score-preview/${auditId}`);
    } catch (err) {
      console.error("Error completing:", err);
    } finally {
      setCompleting(false);
    }
  };

  const isCurrentAnswered = () => {
    if (!currentSection || visibleQuestions.length === 0) return false;
    const q = visibleQuestions[currentQuestion];
    if (!q) return false;
    const sectionAnswers = answers[step] || {};
    return sectionAnswers[q.id] !== undefined && sectionAnswers[q.id] !== "";
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC]" /></div>;
  }

  const activeQ = visibleQuestions[currentQuestion] || null;
  const isConditionalQ = activeQ?.showIf;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="audit-wizard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 md:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-12" />
        </div>
        <div className="flex items-center gap-4">
          {step !== "intro" && saving && <span className="text-xs text-slate-400 font-['IBM_Plex_Sans'] flex items-center gap-1"><Save className="w-3 h-3" /> Saving...</span>}
          {step !== "intro" && <span className="text-xs font-['JetBrains_Mono'] text-slate-500">{progress}% complete</span>}
          <button onClick={() => step === "intro" ? navigate("/") : navigate("/dashboard")} className="text-slate-400 hover:text-slate-600" data-testid="wizard-exit-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Progress bar */}
      {step !== "intro" && (
        <div className="h-1 bg-slate-100 w-full" data-testid="wizard-progress-bar">
          <div className="h-full bg-[#0033CC] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            {step === "intro" ? (
              <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
                <div className="text-center mb-8 sm:mb-10">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-3 sm:mb-4 leading-tight">
                    Let's Build Your<br />AI Growth Engine
                  </h1>
                  <p className="text-sm sm:text-base text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed max-w-lg mx-auto">
                    This quick onboarding blueprint takes 10–15 minutes — and it's one of the highest ROI actions you can take for your business right now.
                  </p>
                </div>

                <div className="bg-white border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
                  <p className="text-sm text-slate-700 font-['IBM_Plex_Sans'] leading-relaxed mb-4">
                    This is an <span className="font-medium text-[#0F172A]">executive discovery process</span> — not a generic form. We're mapping:
                  </p>
                  <div className="space-y-3 mb-4 sm:mb-6">
                    {[
                      { icon: Target, text: "Revenue leaks and operational bottlenecks" },
                      { icon: TrendingUp, text: "AI transformation opportunities by department" },
                      { icon: Rocket, text: "Implementation-ready infrastructure requirements" },
                    ].map((item, i) => (
                      <div key={`idx-${i}`} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#E5EDFF] flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-4 h-4 text-[#0033CC]" strokeWidth={1.5} />
                        </div>
                        <span className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A]">{item.text}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">
                    Think of this as your <span className="font-medium text-[#0F172A]">operational transformation blueprint</span> — the kind of analysis a top consultancy charges $50K+ for.
                  </p>
                </div>

                <div className="bg-[#0F172A] p-4 sm:p-6 mb-4 sm:mb-6">
                  <p className="text-sm text-slate-300 font-['IBM_Plex_Sans'] leading-relaxed mb-4">
                    Your answers fuel an AI-generated transformation blueprint designed to:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {["Quantify your revenue leaks", "Map department-level transformation", "Architect AI infrastructure", "Model implementation ROI"].map((item, i) => (
                      <div key={`idx-${i}`} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#0033CC] flex-shrink-0" />
                        <span className="text-sm font-['IBM_Plex_Sans'] text-white">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 p-4 mb-6 sm:mb-8">
                  <p className="text-sm text-amber-800 font-['IBM_Plex_Sans'] leading-relaxed">
                    <span className="font-medium">Most businesses skip this step.</span> That's why they stay stuck. Take your time. Be honest. The quality of your answers directly impacts the power of the strategy we build for you.
                  </p>
                </div>

                <Button onClick={() => setStep("company_info")} className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-14 sm:h-12 text-sm sm:text-base font-['Outfit'] font-medium flex items-center justify-center gap-2" data-testid="intro-start-btn">
                  <span>Begin Your Blueprint</span> <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </Button>
              </motion.div>
            ) : step === "company_info" ? (
              <motion.div key="company" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Getting Started</p>
                <h2 className="text-2xl sm:text-3xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-2">Tell us about your business</h2>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-8">This helps us tailor the blueprint to your specific context.</p>
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Company Name *</Label>
                    <Input value={companyInfo.company_name} onChange={(e) => setCompanyInfo({ ...companyInfo, company_name: e.target.value })} placeholder="Acme Inc." className="mt-1.5 rounded-none border-slate-300" data-testid="company-name-input" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Website URL *</Label>
                    <Input value={companyInfo.website} onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })} placeholder="https://yourcompany.com" type="url" className="mt-1.5 rounded-none border-slate-300" data-testid="company-website-input" />
                    <p className="text-xs text-slate-400 mt-1 font-['IBM_Plex_Sans']">We use this to personalize your blueprint results</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Describe Your Business *</Label>
                    <Textarea value={companyInfo.business_description} onChange={(e) => setCompanyInfo({ ...companyInfo, business_description: e.target.value })} placeholder="Tell us what your company does, who you serve, and what makes you different. Min 2 sentences." className="mt-1.5 rounded-none border-slate-300 min-h-[80px]" data-testid="company-description-input" />
                    <p className="text-xs text-slate-400 mt-1 font-['IBM_Plex_Sans']">{companyInfo.business_description.length < 40 ? "Min 2 sentences required" : "Looks good"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Industry</Label>
                    <Select value={companyInfo.industry} onValueChange={(v) => setCompanyInfo({ ...companyInfo, industry: v, sub_industry: "" })}>
                      <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map((ind) => (
                          <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {companyInfo.industry && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Sector</Label>
                      <Select value={companyInfo.sub_industry} onValueChange={(v) => setCompanyInfo({ ...companyInfo, sub_industry: v })}>
                        <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="sub-industry-select"><SelectValue placeholder="Select sector" /></SelectTrigger>
                        <SelectContent>
                          {(INDUSTRIES.find(i => i.value === companyInfo.industry)?.subs || []).map((sub) => (
                            <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Company Size</Label>
                    <Select value={companyInfo.company_size} onValueChange={(v) => setCompanyInfo({ ...companyInfo, company_size: v })}>
                      <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="company-size-select"><SelectValue placeholder="Number of employees" /></SelectTrigger>
                      <SelectContent>
                        {["1-5", "6-10", "11-25", "26-50", "51-100", "100+"].map((s) => (
                          <SelectItem key={s} value={s}>{s} employees</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Annual Revenue</Label>
                    <Select value={companyInfo.annual_revenue} onValueChange={(v) => setCompanyInfo({ ...companyInfo, annual_revenue: v })}>
                      <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="revenue-select"><SelectValue placeholder="Select revenue range" /></SelectTrigger>
                      <SelectContent>
                        {["Under $100K", "$100K-$500K", "$500K-$1M", "$1M-$5M", "$5M-$15M", "$15M+"].map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-10">
                  <Button onClick={startAudit} disabled={!companyInfo.company_name || !companyInfo.website || companyInfo.business_description.length < 40} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 h-11 font-['Outfit'] font-medium" data-testid="start-audit-btn">
                    Begin Blueprint <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            ) : currentSection && activeQ ? (
              <motion.div key={`${step}-${activeQ.id}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC]">
                    Section {sectionIndex + 1} of {totalSections}
                  </span>
                  <span className="text-xs text-slate-300">|</span>
                  <span className="text-xs font-['JetBrains_Mono'] text-slate-500">
                    Q{currentQuestion + 1} of {visibleQuestions.length}
                  </span>
                  {isConditionalQ && (
                    <>
                      <span className="text-xs text-slate-300">|</span>
                      <span className="text-xs font-['JetBrains_Mono'] text-amber-600 flex items-center gap-1">
                        <GitBranch className="w-3 h-3" /> Follow-up
                      </span>
                    </>
                  )}
                </div>
                <h2 className="text-xl sm:text-2xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-1">{currentSection.title}</h2>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-8">{currentSection.description}</p>

                {(() => {
                  const q = activeQ;
                  const currentValue = (answers[step] || {})[q.id];

                  if (q.type === "scale") {
                    return (
                      <div data-testid={`question-${q.id}`}>
                        <p className="text-base font-['IBM_Plex_Sans'] font-medium text-[#0F172A] mb-6">{q.text}</p>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((val) => (
                            <button key={val} onClick={() => handleAnswer(q.id, val)}
                              className={`flex-1 p-4 border text-center transition-all ${currentValue === val ? "border-[#0033CC] bg-[#E5EDFF] text-[#0033CC]" : "border-slate-200 hover:border-slate-300 text-slate-600"}`} data-testid={`scale-option-${val}`}>
                              <div className="text-lg font-['Outfit'] font-medium mb-1">{val}</div>
                              <div className="text-xs font-['IBM_Plex_Sans']">{q.labels?.[val - 1] || ""}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  if (q.type === "radio") {
                    return (
                      <div data-testid={`question-${q.id}`}>
                        <p className="text-base font-['IBM_Plex_Sans'] font-medium text-[#0F172A] mb-6">{q.text}</p>
                        <RadioGroup value={String(currentValue || "")} onValueChange={(v) => handleAnswer(q.id, parseInt(v))}>
                          <div className="space-y-3">
                            {q.options.map((opt) => (
                              <label key={opt.value} className={`flex items-center gap-4 p-4 border cursor-pointer transition-all ${String(currentValue) === opt.value ? "border-[#0033CC] bg-[#E5EDFF]" : "border-slate-200 hover:border-slate-300"}`} data-testid={`radio-option-${opt.value}`}>
                                <RadioGroupItem value={opt.value} className="border-slate-300 text-[#0033CC]" />
                                <span className="text-sm font-['IBM_Plex_Sans'] text-slate-700">{opt.label}</span>
                              </label>
                            ))}
                          </div>
                        </RadioGroup>
                      </div>
                    );
                  }
                  return null;
                })()}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      {step !== "company_info" && step !== "intro" && (
        <div className="bg-white border-t border-slate-200 px-6 md:px-12 py-4 flex items-center justify-between relative z-[9999]">
          <Button variant="ghost" onClick={handleBack} className="text-slate-600 font-['Outfit']" data-testid="wizard-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button onClick={handleNext} disabled={!isCurrentAnswered() || completing}
            className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 h-11 font-['Outfit'] font-medium disabled:opacity-40"
            data-testid="wizard-next-btn">
            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> :
              sectionIndex === totalSections - 1 && currentQuestion === visibleQuestions.length - 1 ?
                <span>Complete Audit <ArrowRight className="w-4 h-4 ml-2 inline" /></span> :
                <span>Next <ArrowRight className="w-4 h-4 ml-2 inline" /></span>
            }
          </Button>
        </div>
      )}
    </div>
  );
}
